<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <CompanyAppLayout>
      <!-- Page Header -->
      <div class="pt-8 pb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-white mb-2">Company Expenses</h1>
            <p class="text-slate-400">Track and manage company expenses, reimbursements, and financial records</p>
          </div>
          <div class="flex items-center space-x-3">
            <button @click="showAddExpenseModal = true" class="btn-primary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
              </svg>
              Add Expense
            </button>
            <button @click="exportExpenses" class="btn-secondary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              Export
            </button>
          </div>
        </div>
      </div>

      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Total Expenses</p>
            <p class="text-3xl font-bold text-white">${{ expenseStats.totalExpenses.toLocaleString() }}</p>
            <p class="text-sm text-emerald-400">this month</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Pending Approval</p>
            <p class="text-3xl font-bold text-white">{{ expenseStats.pendingApproval }}</p>
            <p class="text-sm text-cyan-400">expenses</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Reimbursements</p>
            <p class="text-3xl font-bold text-white">${{ expenseStats.reimbursements.toLocaleString() }}</p>
            <p class="text-sm text-amber-400">this month</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Approved</p>
            <p class="text-3xl font-bold text-white">{{ expenseStats.approvedExpenses }}</p>
            <p class="text-sm text-purple-400">this month</p>
          </div>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 mb-8">
        <h2 class="text-xl font-semibold text-white mb-4">Quick Actions</h2>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <button @click="showAddExpenseModal = true" class="quick-action-card">
            <svg class="w-8 h-8 text-emerald-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
            </svg>
            <span class="text-sm font-medium text-white">Add Expense</span>
          </button>
          
          <button @click="showReimbursementModal = true" class="quick-action-card">
            <svg class="w-8 h-8 text-cyan-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
            </svg>
            <span class="text-sm font-medium text-white">Request Reimbursement</span>
          </button>
          
          <button @click="generateExpenseReport" class="quick-action-card">
            <svg class="w-8 h-8 text-amber-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            <span class="text-sm font-medium text-white">Generate Report</span>
          </button>
          
          <button @click="showBudgetModal = true" class="quick-action-card">
            <svg class="w-8 h-8 text-purple-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
            </svg>
            <span class="text-sm font-medium text-white">Set Budget</span>
          </button>
        </div>
      </div>

      <!-- Expense Categories -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Recent Expenses -->
        <div class="lg:col-span-2">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-xl font-semibold text-white">Recent Expenses</h2>
              <div class="flex items-center space-x-2">
                <input 
                  v-model="searchTerm" 
                  type="text" 
                  placeholder="Search expenses..."
                  class="form-input w-48"
                />
                <select v-model="filterStatus" class="form-select">
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            <div class="space-y-4">
              <div v-for="expense in filteredExpenses" :key="expense.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl">
                <div class="flex items-center space-x-4">
                  <div class="w-10 h-10 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center">
                    <span class="text-sm font-bold text-white">{{ expense.category.charAt(0) }}</span>
                  </div>
                  <div>
                    <h3 class="text-sm font-medium text-white">{{ expense.description }}</h3>
                    <p class="text-xs text-slate-400">{{ expense.employee }} • {{ formatDate(expense.date) }}</p>
                  </div>
                </div>
                <div class="flex items-center space-x-4">
                  <div class="text-right">
                    <p class="text-sm font-medium text-white">${{ expense.amount.toLocaleString() }}</p>
                    <p class="text-xs text-slate-400">{{ expense.category }}</p>
                  </div>
                  <span :class="[
                    'px-2 py-1 rounded-full text-xs font-medium',
                    expense.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                    expense.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-red-500/20 text-red-400'
                  ]">
                    {{ expense.status }}
                  </span>
                  <div class="flex items-center space-x-2">
                    <button @click="viewExpenseDetails(expense)" class="text-emerald-400 hover:text-emerald-300">
                      View
                    </button>
                    <button @click="approveExpense(expense.id)" class="text-cyan-400 hover:text-cyan-300">
                      Approve
                    </button>
                    <button
                      class="text-red-400 hover:text-red-300 disabled:opacity-50"
                      :disabled="deletingExpenseId === expense.id"
                      @click="deleteExpense(expense)"
                    >
                      {{ deletingExpenseId === expense.id ? 'Deleting…' : 'Delete' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Expense Summary -->
        <div class="lg:col-span-1">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h3 class="text-lg font-semibold text-white mb-4">Expense Summary</h3>
            
            <div class="space-y-4">
              <div v-for="category in expenseCategories" :key="category.name" class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div class="w-3 h-3 rounded-full" :style="{ backgroundColor: category.color }"></div>
                  <span class="text-sm text-slate-300">{{ category.name }}</span>
                </div>
                <div class="text-right">
                  <p class="text-sm font-medium text-white">${{ category.amount.toLocaleString() }}</p>
                  <p class="text-xs text-slate-400">{{ category.percentage }}%</p>
                </div>
              </div>
            </div>

            <div class="border-t border-slate-700/50 mt-6 pt-4">
              <div class="flex items-center justify-between">
                <span class="text-lg font-semibold text-white">Total</span>
                <span class="text-lg font-bold text-emerald-400">${{ totalExpenses.toLocaleString() }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Expense Reports -->
      <div class="mt-8">
        <h2 class="text-xl font-semibold text-white mb-6">Expense Reports</h2>
        <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-slate-700/50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Report ID</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Employee</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Period</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Total Amount</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-700/50">
                <tr v-for="report in expenseReports" :key="report.id" class="hover:bg-slate-700/30">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ report.id }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-white">{{ report.employee }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ report.period }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">${{ report.totalAmount.toLocaleString() }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="[
                      'px-2 py-1 rounded-full text-xs font-medium',
                      report.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                      report.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-red-500/20 text-red-400'
                    ]">
                      {{ report.status }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div class="flex items-center space-x-2">
                      <button @click="viewReportDetails(report)" class="text-emerald-400 hover:text-emerald-300">
                        View
                      </button>
                      <button @click="approveReport(report.id)" class="text-cyan-400 hover:text-cyan-300">
                        Approve
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Add Expense Modal -->
      <div v-if="showAddExpenseModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl">
          <h3 class="text-xl font-semibold text-white mb-4">Add Company Expense</h3>
          
          <form @submit.prevent="submitExpense" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Expense Category</label>
                <select v-model="expenseData.category" class="form-select w-full" required>
                  <option value="">Select Category</option>
                  <option value="travel">Travel</option>
                  <option value="meals">Meals & Entertainment</option>
                  <option value="office">Office Supplies</option>
                  <option value="equipment">Equipment</option>
                  <option value="utilities">Utilities</option>
                  <option value="insurance">Insurance</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Amount</label>
                <input v-model="expenseData.amount" type="number" step="0.01" class="form-input w-full" required />
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
              <input v-model="expenseData.description" type="text" class="form-input w-full" required />
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Date</label>
                <input v-model="expenseData.date" type="date" class="form-input w-full" required />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Employee</label>
                <select v-model="expenseData.employee" class="form-select w-full" required>
                  <option value="">Select Employee</option>
                  <option v-for="employee in employees" :key="employee.id" :value="employee.name">{{ employee.name }}</option>
                </select>
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Notes</label>
              <textarea v-model="expenseData.notes" class="form-input w-full" rows="3"></textarea>
            </div>
            
            <div class="flex items-center justify-end space-x-3 pt-4">
              <button type="button" @click="showAddExpenseModal = false" class="btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn-primary">
                Add Expense
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Request Reimbursement Modal -->
      <div v-if="showReimbursementModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl">
          <h3 class="text-xl font-semibold text-white mb-4">Request Reimbursement</h3>
          
          <form @submit.prevent="submitReimbursement" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Expense Category</label>
                <select v-model="reimbursementData.category" class="form-select w-full" required>
                  <option value="">Select Category</option>
                  <option value="travel">Travel</option>
                  <option value="meals">Meals & Entertainment</option>
                  <option value="office">Office Supplies</option>
                  <option value="equipment">Equipment</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Amount</label>
                <input v-model="reimbursementData.amount" type="number" step="0.01" class="form-input w-full" required />
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
              <input v-model="reimbursementData.description" type="text" class="form-input w-full" required />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Date Incurred</label>
              <input v-model="reimbursementData.date" type="date" class="form-input w-full" required />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Justification</label>
              <textarea v-model="reimbursementData.justification" class="form-input w-full" rows="3" required></textarea>
            </div>
            
            <div class="flex items-center justify-end space-x-3 pt-4">
              <button type="button" @click="showReimbursementModal = false" class="btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn-primary">
                Submit Request
              </button>
            </div>
          </form>
        </div>
      </div>
    </CompanyAppLayout>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useUserStore } from '@/stores/userStore'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'

const userStore = useUserStore()

// Reactive data
const showAddExpenseModal = ref(false)
const showReimbursementModal = ref(false)
const showBudgetModal = ref(false)
const searchTerm = ref('')
const filterStatus = ref('')
const deletingExpenseId = ref<number | null>(null)

const expenseStats = ref({
  totalExpenses: 0,
  pendingApproval: 0,
  reimbursements: 0,
  approvedExpenses: 0
})

const expenses = ref([])
const expenseReports = ref([])
const employees = ref([])

const expenseData = ref({
  category: '',
  amount: '',
  description: '',
  date: '',
  employee: '',
  notes: ''
})

const reimbursementData = ref({
  category: '',
  amount: '',
  description: '',
  date: '',
  justification: ''
})

// Computed
const filteredExpenses = computed(() => {
  let filtered = expenses.value

  if (searchTerm.value) {
    const search = searchTerm.value.toLowerCase()
    filtered = filtered.filter(exp => 
      exp.description.toLowerCase().includes(search) ||
      exp.employee.toLowerCase().includes(search) ||
      exp.category.toLowerCase().includes(search)
    )
  }

  if (filterStatus.value) {
    filtered = filtered.filter(exp => exp.status === filterStatus.value)
  }

  return filtered
})

const expenseCategories = computed(() => {
  const categories = {}
  expenses.value.forEach(exp => {
    if (!categories[exp.category]) {
      categories[exp.category] = { name: exp.category, amount: 0, count: 0 }
    }
    categories[exp.category].amount += exp.amount
    categories[exp.category].count++
  })

  const total = Object.values(categories).reduce((sum: any, cat: any) => sum + cat.amount, 0)
  
  return Object.values(categories).map((cat: any) => ({
    ...cat,
    percentage: total > 0 ? Math.round((cat.amount / total) * 100) : 0,
    color: getCategoryColor(cat.name)
  }))
})

const totalExpenses = computed(() => {
  return expenses.value.reduce((sum, exp) => sum + exp.amount, 0)
})

// Methods
const fetchExpenseStats = async () => {
  try {
    const response = await fetch('/api/company/expense-stats', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      expenseStats.value = data
    }
  } catch (error) {
    console.error('Error fetching expense stats:', error)
  }
}

const fetchExpenses = async () => {
  try {
    const response = await fetch('/api/company/expenses', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      expenses.value = data.expenses || []
    }
  } catch (error) {
    console.error('Error fetching expenses:', error)
  }
}

const fetchExpenseReports = async () => {
  try {
    const response = await fetch('/api/company/expense-reports', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      expenseReports.value = data.reports || []
    }
  } catch (error) {
    console.error('Error fetching expense reports:', error)
  }
}

const fetchEmployees = async () => {
  try {
    const response = await fetch('/api/company/employees', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      employees.value = data.employees || []
    }
  } catch (error) {
    console.error('Error fetching employees:', error)
  }
}

const submitExpense = async () => {
  try {
    const response = await fetch('/api/company/add-expense', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(expenseData.value)
    })
    
    if (response.ok) {
      showAddExpenseModal.value = false
      expenseData.value = { category: '', amount: '', description: '', date: '', employee: '', notes: '' }
      await fetchExpenses()
      await fetchExpenseStats()
    }
  } catch (error) {
    console.error('Error submitting expense:', error)
  }
}

const submitReimbursement = async () => {
  try {
    const response = await fetch('/api/company/request-reimbursement', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(reimbursementData.value)
    })
    
    if (response.ok) {
      showReimbursementModal.value = false
      reimbursementData.value = { category: '', amount: '', description: '', date: '', justification: '' }
      await fetchExpenses()
      await fetchExpenseStats()
    }
  } catch (error) {
    console.error('Error submitting reimbursement:', error)
  }
}

const deleteExpense = async (expense: any) => {
  if (!window.confirm(`Delete expense “${expense.description || 'this item'}”? This cannot be undone.`)) return
  deletingExpenseId.value = expense.id
  try {
    const response = await fetch(`/api/company/expenses/${expense.id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json',
      },
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`)
    expenses.value = expenses.value.filter((item: any) => item.id !== expense.id)
    await fetchExpenseStats()
  } catch (error) {
    console.error('Error deleting expense:', error)
  } finally {
    deletingExpenseId.value = null
  }
}

const approveExpense = async (expenseId: number) => {
  try {
    const response = await fetch('/api/company/approve-expense', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ expenseId })
    })
    
    if (response.ok) {
      await fetchExpenses()
      await fetchExpenseStats()
    }
  } catch (error) {
    console.error('Error approving expense:', error)
  }
}

const approveReport = async (reportId: number) => {
  try {
    const response = await fetch('/api/company/approve-report', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ reportId })
    })
    
    if (response.ok) {
      await fetchExpenseReports()
    }
  } catch (error) {
    console.error('Error approving report:', error)
  }
}

const viewExpenseDetails = (expense: any) => {
  // Navigate to expense details page
}

const viewReportDetails = (report: any) => {
  // Navigate to report details page
}

const generateExpenseReport = () => {
  // Generate expense report
}

const exportExpenses = () => {
  const csvContent = [
    ['Date', 'Employee', 'Category', 'Description', 'Amount', 'Status'],
    ...filteredExpenses.value.map(exp => [
      formatDate(exp.date),
      exp.employee,
      exp.category,
      exp.description,
      `$${exp.amount.toLocaleString()}`,
      exp.status
    ])
  ].map(row => row.join(',')).join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'company-expenses.csv'
  a.click()
  window.URL.revokeObjectURL(url)
}

const getCategoryColor = (category: string) => {
  const colors = {
    travel: '#3B82F6',
    meals: '#10B981',
    office: '#F59E0B',
    equipment: '#EF4444',
    utilities: '#8B5CF6',
    insurance: '#06B6D4',
    other: '#6B7280'
  }
  return colors[category] || '#6B7280'
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString()
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchExpenseStats(),
    fetchExpenses(),
    fetchExpenseReports(),
    fetchEmployees()
  ])
})
</script>

<style scoped>
.stat-card {
  @apply bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6;
}

.quick-action-card {
  @apply flex flex-col items-center justify-center p-4 bg-slate-700/30 rounded-xl hover:bg-slate-600/50 transition-colors;
}

.btn-primary {
  @apply px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors flex items-center;
}

.btn-secondary {
  @apply px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 font-medium rounded-lg transition-colors flex items-center;
}

.form-input, .form-select {
  @apply bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent;
}

.form-select {
  @apply min-w-[120px];
}
</style>
